import {
  loadRestoreState,
} from "./map-page-state.js";
import {
  cloneBoundedMapShellSignalPatch,
  createMapShellProjectionOwner,
  mapShellSignalStateV1FromProjection,
} from "./map-shell-projection.js";
import {
  FISHYMAP_SIGNALS_READY_EVENT,
  mapShellSignalsAreReady,
  resolveMapPageShell,
} from "./map-shell-lifecycle.js";
import {
  FISHYMAP_SIGNAL_PATCHED_EVENT,
  runWithProgrammaticSignalPatch,
} from "./map-signal-patch.js";

export { FISHYMAP_SIGNALS_READY_EVENT } from "./map-shell-lifecycle.js";

export function createMapPageLive({
  globalRef = globalThis,
  signalAdapter,
  deferAfterDatastarCallback = (callback) => {
    const defer = globalRef.queueMicrotask ?? globalThis.queueMicrotask;
    return defer.call(globalRef, callback);
  },
} = {}) {
  if (
    !signalAdapter
    || typeof signalAdapter.snapshot !== "function"
    || typeof signalAdapter.apply !== "function"
  ) {
    throw new TypeError("Map page live requires the bounded Datastar signal adapter");
  }
  if (typeof deferAfterDatastarCallback !== "function") {
    throw new TypeError("Map page live callback deferral owner must be a function");
  }

  const projectionSubscribers = new Set();
  const patchSubscribers = new Set();
  const state = {
    shell: null,
    projectionOwner: null,
    signalState: null,
    started: false,
    stopped: false,
    connectScheduled: false,
    restoreSettled: false,
    restorePromise: null,
    resolveRestore: null,
    rejectRestore: null,
    generation: 0,
  };
  state.restorePromise = new Promise((resolve, reject) => {
    state.resolveRestore = resolve;
    state.rejectRestore = reject;
  });

  function current() {
    return state.projectionOwner?.current() ?? null;
  }

  function currentSignals() {
    return state.signalState;
  }

  function publishProjection() {
    const projection = current();
    if (!projection) {
      return;
    }
    for (const subscriber of [...projectionSubscribers]) {
      subscriber(projection);
    }
  }

  function publishPatch(patch) {
    for (const subscriber of [...patchSubscribers]) {
      subscriber(patch);
    }
  }

  function currentLocationHref() {
    return globalRef.location?.href || globalRef.window?.location?.href || "";
  }

  function replaceProjectionFromDatastar(signals, { publish = true } = {}) {
    if (state.projectionOwner) {
      state.projectionOwner.replaceFromSignals(signals);
    } else {
      state.projectionOwner = createMapShellProjectionOwner(signals);
    }
    state.signalState = mapShellSignalStateV1FromProjection(current());
    if (publish) {
      publishProjection();
    }
    return current();
  }

  function patchSignals(patch, {
    publish = true,
    notify = true,
    beforeNotify = null,
  } = {}) {
    const signals = currentSignals();
    if (!signals || !state.shell) {
      throw new Error("Map page signals are not ready");
    }
    if (notify && beforeNotify != null && typeof beforeNotify !== "function") {
      throw new TypeError("Map page patch beforeNotify hook must be a function");
    }
    const safePatch = cloneBoundedMapShellSignalPatch(patch);
    runWithProgrammaticSignalPatch(
      state.shell,
      () => signalAdapter.apply(signals, safePatch),
    );
    state.projectionOwner.patch(safePatch);
    state.signalState = mapShellSignalStateV1FromProjection(current());
    if (notify) {
      beforeNotify?.(safePatch);
    }
    if (publish) {
      publishProjection();
    }
    if (notify) {
      publishPatch(safePatch);
    }
    return true;
  }

  function restore() {
    replaceProjectionFromDatastar(signalAdapter.snapshot(), { publish: false });
    const restoreState = loadRestoreState({
      localStorage: globalRef.localStorage,
      locationHref: currentLocationHref(),
    });
    if (restoreState.uiPatch) {
      patchSignals(restoreState.uiPatch, { publish: false, notify: false });
    }
    if (restoreState.bookmarkPatch) {
      patchSignals(restoreState.bookmarkPatch, { publish: false, notify: false });
    }
    publishProjection();
    if (!state.restoreSettled) {
      state.restoreSettled = true;
      state.resolveRestore?.(current());
    }
    return current();
  }

  function reportDeferredFailure(error) {
    if (!state.restoreSettled) {
      state.restoreSettled = true;
      state.rejectRestore?.(error);
      return;
    }
    if (typeof globalRef.reportError === "function") {
      globalRef.reportError(error);
      return;
    }
    deferAfterDatastarCallback(() => {
      throw error;
    });
  }

  function scheduleConnect() {
    if (state.connectScheduled || state.stopped || current()) {
      return;
    }
    state.connectScheduled = true;
    const generation = state.generation;
    deferAfterDatastarCallback(() => {
      state.connectScheduled = false;
      if (state.stopped || generation !== state.generation || current()) {
        return;
      }
      try {
        restore();
      } catch (error) {
        reportDeferredFailure(error);
      }
    });
  }

  function handleSignalsReady() {
    scheduleConnect();
  }

  function handleFilteredSignalPatch(event) {
    const patch = event?.detail;
    const generation = state.generation;
    deferAfterDatastarCallback(() => {
      if (state.stopped || generation !== state.generation) {
        return;
      }
      try {
        const safePatch = cloneBoundedMapShellSignalPatch(patch);
        if (current()) {
          state.projectionOwner.patch(safePatch);
          state.signalState = mapShellSignalStateV1FromProjection(current());
          publishProjection();
        } else {
          restore();
        }
        publishPatch(safePatch);
      } catch (error) {
        reportDeferredFailure(error);
      }
    });
  }

  function start() {
    if (state.started) {
      return true;
    }
    state.shell = resolveMapPageShell(globalRef);
    if (!state.shell) {
      return false;
    }
    state.started = true;
    state.stopped = false;
    state.shell.addEventListener(FISHYMAP_SIGNALS_READY_EVENT, handleSignalsReady);
    state.shell.addEventListener(
      FISHYMAP_SIGNAL_PATCHED_EVENT,
      handleFilteredSignalPatch,
    );
    if (mapShellSignalsAreReady(state.shell)) {
      scheduleConnect();
    }
    return true;
  }

  function stop() {
    if (!state.started) {
      return false;
    }
    state.generation += 1;
    state.stopped = true;
    state.started = false;
    state.connectScheduled = false;
    state.shell?.removeEventListener(
      FISHYMAP_SIGNALS_READY_EVENT,
      handleSignalsReady,
    );
    state.shell?.removeEventListener(
      FISHYMAP_SIGNAL_PATCHED_EVENT,
      handleFilteredSignalPatch,
    );
    projectionSubscribers.clear();
    patchSubscribers.clear();
    return true;
  }

  function subscribe(set, subscriber, { emitCurrent = false } = {}) {
    if (typeof subscriber !== "function") {
      throw new TypeError("Map page live subscriber must be a function");
    }
    set.add(subscriber);
    if (emitCurrent && set === projectionSubscribers && current()) {
      subscriber(current());
    }
    return () => set.delete(subscriber);
  }

  return Object.freeze({
    current,
    currentSignals,
    patchSignals,
    start,
    stop,
    subscribe(subscriber, options = {}) {
      return subscribe(projectionSubscribers, subscriber, {
        emitCurrent: options.emitCurrent ?? true,
      });
    },
    subscribePatches(subscriber) {
      return subscribe(patchSubscribers, subscriber);
    },
    whenRestored() {
      return state.restorePromise;
    },
  });
}
